import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

// https://primevue.org/theming/styled/#presets
const GenoplanPreset = definePreset(Aura, {
  primitive: {
    grey: {
      50: '#f8f9fa',
      100: '#f1f3f5',
      200: '#e2e5e9',
      300: '#d6dbe0',
      400: '#b5bdc5',
      500: '#89949f',
      600: '#6e7a87',
      700: '#57616b',
      800: '#454d54',
      900: '#2e3338',
    },
    red: {
      50: '#fdeae9',
      100: '#fbcfcd',
      200: '#fab4b0',
      300: '#f99892',
      400: '#fa7b73',
      500: '#fb5e54',
      600: '#d7554c',
      700: '#b44b44',
      800: '#923f3a',
      900: '#713430',
    },
    orange: {
      50: '#fdf0e3',
      100: '#fbddc1',
      200: '#fbca9d',
      300: '#fbb778',
      400: '#fda452',
      500: '#ff912b',
      600: '#da7e2a',
      700: '#b66c27',
      800: '#935923',
      900: '#71461e',
    },
    green: {
      50: '#e4f5ea',
      100: '#c1e8d1',
      200: '#9eddb7',
      300: '#7bd29e',
      400: '#56c884',
      500: '#31be6a',
      600: '#2da35d',
      700: '#29884f',
      800: '#246e42',
      900: '#1e5534',
    },
    verdigris: {
      50: '#e6f3f3',
      100: '#c6e5e4',
      200: '#a6d8d6',
      300: '#86cbc8',
      400: '#65bfbb',
      500: '#43b3ae',
      600: '#3d9a95',
      700: '#35817d',
      800: '#2e6966',
      900: '#25514f',
    },
    blue: {
      50: '#e4f0fb',
      100: '#c2def6',
      200: '#a0cbf3',
      300: '#7cb9f0',
      400: '#57a6ef',
      500: '#3294ee',
      600: '#2f81cb',
      700: '#2b6daa',
      800: '#265a89',
      900: '#20476a',
    },
    indigo: {
      50: '#e6eaf8',
      100: '#c6d0f0',
      200: '#a6b5e9',
      300: '#859ae2',
      400: '#637edc',
      500: '#4162d7',
      600: '#3c57b8',
      700: '#354b9a',
      800: '#2e3f7d',
      900: '#263361',
    },
    purple: {
      50: '#f3e9f9',
      100: '#e5cef3',
      200: '#d7b2ed',
      300: '#c995e8',
      400: '#bb78e4',
      500: '#ad5ae0',
      600: '#9651c0',
      700: '#7f47a1',
      800: '#683c83',
      900: '#523166',
    },
  },
  semantic: {
    primary: {
      50: '{verdigris.50}',
      100: '{verdigris.100}',
      200: '{verdigris.200}',
      300: '{verdigris.300}',
      400: '{verdigris.400}',
      500: '{verdigris.500}',
      600: '{verdigris.600}',
      700: '{verdigris.700}',
      800: '{verdigris.800}',
      900: '{verdigris.900}',
      950: '{verdigris.950}',
    },
    formField: {
      disabledBackground: '{grey.50}', // https://www.figma.com/file/CJ5DHEgZd3K295bzKTL1Dm/Patients-(Genoplan-HT)?type=design&node-id=7-5030&mode=dev
      disabledColor: '{grey.500}',
      paddingX: '0.875rem',
      paddingY: '1rem',
      smFontSize: '0.875rem',
      smPaddingX: '0.65625rem',
      smPaddingY: '0.5rem',
    },
    overlay: {
      select: {
        borderRadius: 0,
      },
    },
    content: {
      // menu dropdown
      borderRadius: 0,
    },
    navigation: {
      item: {
        // menu item
        borderRadius: 0,
      },
    },
    list: {
      padding: '0', // select options
      gap: '0', // select options
      option: {
        borderRadius: 0, // select options
      },
    },
    radiobutton: {
      smWidth: '0.9375rem',
      smHeight: '0.9375rem',
      iconSmSize: '0.5625rem',
    },
    textarea: {
      smFontSize: '0.875rem',
      smPaddingX: '0.65625rem',
      smPaddingY: '0.4375rem',
    },
    colorScheme: {
      light: {
        formField: {
          color: '{grey.900}',
          placeholderColor: '{grey.500}',
          iconColor: '{grey.900}',
        },
        highlight: {
          // select options
          background: '{primary.500}',
          color: 'white',
          focusBackground: '{primary.500}',
          focusColor: 'white',
        },
        text: {
          color: '{grey.900}',
          hoverColor: '{grey.900}',
          mutedColor: '{grey.500}',
          hoverMutedColor: '{grey.600}',
        },
      },
    },
  },
  components: {
    // https://www.figma.com/design/QlHB3zLPBCurx53YjI8lux/Components-(KDTC%2C-Pet)?node-id=36-354&t=rKFp9YuKzeqHrrNO-4
    autocomplete: {
      colorScheme: {
        light: {
          overlayBorderRadius: '0.375rem',
        },
      },
    },
    button: {
      colorScheme: {
        light: {
          smPaddingX: '1rem',
          smPaddingY: '0.5rem',
          lgPaddingX: '1.125rem',
          lgPaddingY: '1rem',
          lgFontSize: '1rem',
          secondaryColor: '{grey.900}',
          textSecondaryColor: '{grey.900}',
          outlinedSecondaryColor: '{grey.900}',
          secondaryBackground: '{grey.100}', // https://www.figma.com/file/04SlAQDbb2M2yNzPgjjpvF/DNA-PLAN-(Mobile)-1.1?type=design&node-id=5782-6147&mode=design&t=QzNVvy3QEdc9pqoI-4
          iconOnlyWidth: '2.75rem',
        },
      },
    },
    dialog: {
      colorScheme: {
        light: {
          borderRadius: '0.1875rem',
        },
      },
    },
    drawer: {
      colorScheme: {
        light: {
          contentPadding: '0 1.5rem 1.5rem',
          headerPadding: '0 1.5rem',
        },
      },
    },
    inputtext: {
      colorScheme: {
        light: {
          disabledBackground: '{grey.50}',
          disabledColor: '{grey.400}',
          smPaddingX: '0.625rem',
          smPaddingY: '0.65625rem',
        },
      },
    },
    menu: {
      colorScheme: {
        light: {
          listPadding: '0.5rem 0',
          itemPadding: '0.75rem 1.25rem',
        },
      },
    },
    paginator: {
      colorScheme: {
        light: {
          navButtonBorderRadius: '0.1875rem',
          navButtonWidth: '2.625rem',
          navButtonHeight: '2.625rem',
        },
      },
    },
    tabs: {
      colorScheme: {
        light: {
          tabFontWeight: 500,
          tabPadding: '0.375rem 0', // [.co.kr]의 find-account/index.vue의 tabs과 값이 다름
          tabpanelPadding: '0', // y축 2rem이 들어가야하는데 bug 때문에 index 0 padding이 index 1에 나옴. 해서 padding을 없애고 tabpanel 내에서 처리
          tabBorderWidth: '0 0 2px 0',
        },
      },
    },
    toast: {
      // https://www.figma.com/design/04SlAQDbb2M2yNzPgjjpvF/Genoplan-X-(Mobile)-1.1?node-id=9338-27430&t=JJQTq2lWFlpI8ZKL-11
      colorScheme: {
        light: {
          borderWidth: 0,
          borderRadius: '0.5rem',
          contentPadding: '0.5rem 1rem',
          // severity별 style은 custom style 때문에 css로 처리
          width: 'max-content',
        },
      },
    },
    togglebutton: {
      // GIT-5381
      colorScheme: {
        light: {
          contentSmPadding: '0',
          contentCheckedBackground: '{primary.500}',
          transitionDuration: '0',
        },
      },
    },
  },
})

const KO_DAYS = ['일', '월', '화', '수', '목', '금', '토'] // https://app.asana.com/0/0/1208300074055657/1181330478234348/f
const KO_MONTHS = [
  '1월',
  '2월',
  '3월',
  '4월',
  '5월',
  '6월',
  '7월',
  '8월',
  '9월',
  '10월',
  '11월',
  '12월',
]
export function blockNavOnFullDrawer(visible) {
  /*
   * 모바일에 팝업창이 화면 가득, 팝업이란걸 인지하지 못한채 뒤로가기를 누르게 됨 GIT-5374
   * nav 이동 대신 팝업이 꺼지도록
   * onBeforeRouteLeave에 return과 함께 사용
   */
  if (!visible.value) return true

  visible.value = false
  return false
}

export const nuxtPrimevueOptions = {
  locale: {
    dayNamesMin: KO_DAYS,
    dayNamesShort: KO_DAYS,
    monthNames: KO_MONTHS,
    monthNamesShort: KO_MONTHS,
  },
  ripple: false,
  theme: {
    preset: GenoplanPreset,
    options: {
      darkModeSelector: '.dark-mode',
      cssLayer: {
        name: 'primevue',
        order:
          'theme, base, primevue, primevue-custom, components, components-custom, utilities',
      },
    },
  },
}
